// Shared, trusted response validation and objective comparison (Learner Work
// and Response Policy v1 §5). The learner gateway is authoritative for saved
// outcomes and imports this module; the Learner App uses the same code only
// where no record is written (teacher preview, My Learning practice), so the
// two sides can never hold divergent answer rules.
//
// It reads the raw version 1 step definition, never an adapted screen step.
// A step whose expected answer has been withheld cannot be compared here; the
// caller must not ask.

import {
  isNegative,
  isNumericValue,
  numericEqual,
} from "../maths/numeric.js";
import {
  BOUNDED_OPERATORS,
  expressionMatches,
  symbolsIn,
} from "../maths/expression.js";
import { geometryCorrect } from "../maths/coordinates.js";

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const list = (value) => (Array.isArray(value) ? value : []);
const hasText = (value) => typeof value === "string" && value.trim() !== "";
const sameArray = (a, b) =>
  Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i]);
const sameValueMap = (actual, expected) => {
  if (!isObject(actual) || !isObject(expected)) return false;
  const keys = Object.keys(expected).sort();
  return sameArray(Object.keys(actual).sort(), keys) && keys.every((key) => actual[key] === expected[key]);
};
const sameRelations = (expected, actual, left, right) => {
  if (!Array.isArray(expected) || !Array.isArray(actual) || expected.length !== actual.length) return false;
  const key = (entry) => `${entry?.[left]}\u0000${entry?.[right]}`;
  const wanted = new Set(expected.map(key));
  const got = new Set(actual.map(key));
  return got.size === wanted.size && [...got].every((value) => wanted.has(value));
};

export const canonicalType = (step) => {
  const value = String(step?.type ?? step?.kind ?? "").toLowerCase();
  return {
    recognise: "choice",
    recognize: "choice",
    order: "sequence",
    draw: "drawing",
  }[value] || value;
};

const choiceExpected = (step) =>
  step.expected ?? step.correct ?? step.answer ?? step.correct_value;
const sequenceExpected = (step) =>
  step.expected ?? step.correct_order ?? step.correct ?? step.answer;

const numericOutcome = (step, response) => {
  const value = response?.value;
  const answer = step.answer || {};
  const expected = list(step.expected?.values);
  if (!isNumericValue(value)) return null;
  const representations = list(answer.representations);
  if (representations.length && !representations.includes(value.kind)) return null;
  if (answer.allow_negative === false && isNegative(value)) return null;
  const units = list(answer.unit_options);
  const fixedUnit = answer.unit?.value;
  if (fixedUnit && (response.unit || fixedUnit) !== fixedUnit) return null;
  if (units.length && !units.some((unit) => unit?.value === response.unit)) return null;
  if (!fixedUnit && !units.length && response?.unit != null) return null;
  const expectedUnit = step.expected?.unit ?? fixedUnit ?? null;
  if ((response?.unit ?? fixedUnit ?? null) !== expectedUnit) return "incorrect";
  return expected.some((candidate) => numericEqual(value, candidate, step.expected?.tolerance || null))
    ? "correct"
    : "incorrect";
};

const expressionOutcome = (step, response) => {
  if (!isObject(response?.expression)) return null;
  const answer = step.answer || {};
  const accepted = list(step.expected?.accepted);
  if (!accepted.length) return null;
  return expressionMatches(response.expression, accepted, {
    symbols: list(answer.symbols),
    operators: list(answer.operators),
    allowEquation: answer.allow_equation === true,
  })
    ? "correct"
    : "incorrect";
};

const tableOutcome = (step, response) => {
  if (!isObject(response?.values) || !isObject(step.expected?.values)) return null;
  const cells = new Map();
  for (const row of list(step.rows)) {
    for (const cell of list(row?.cells)) cells.set(cell?.id, cell);
  }
  for (const [cellId, expectation] of Object.entries(step.expected.values)) {
    const cell = cells.get(cellId);
    const value = response.values[cellId];
    if (!cell?.editable || value === undefined) return null;
    if (cell.entry_type === "numeric") {
      if (!isNumericValue(value)) return null;
      if (!list(expectation?.values).some((candidate) => numericEqual(value, candidate, expectation?.tolerance || null))) {
        return "incorrect";
      }
    } else if (cell.entry_type === "expression") {
      const accepted = list(expectation?.accepted);
      const symbols = symbolsIn(accepted.map((item) => item?.source || "").join(" "));
      if (!expressionMatches(value, accepted, { symbols, operators: BOUNDED_OPERATORS, allowEquation: false })) {
        return "incorrect";
      }
    } else return null;
  }
  return "correct";
};

const textOutcome = (step, response) => {
  if (!isObject(response) || typeof response.text !== "string") return null;
  const length = [...response.text].length;
  const min = Number.isInteger(step.min_length) ? step.min_length : 0;
  const max = Number.isInteger(step.max_length) ? step.max_length : 4000;
  if (length < min || length > max || max < 1 || max > 4000) return null;
  const accepted = step.expected?.accepted;
  if (accepted === undefined) return "ungraded";
  if (step.mode !== "short" || !Array.isArray(accepted) || !accepted.every(hasText)) return null;
  const compare = step.expected?.comparison || "exact";
  const normalise = (value) => {
    if (compare === "trimmed_case_insensitive") return value.trim().toLocaleLowerCase("en");
    if (compare === "trimmed") return value.trim();
    if (compare === "exact") return value;
    return null;
  };
  if (!["trimmed_case_insensitive", "trimmed", "exact"].includes(compare)) return null;
  return accepted.some((value) => normalise(value) === normalise(response.text))
    ? "correct"
    : "incorrect";
};

// The one objective comparison for every primitive. Returns
// { type, outcome } with outcome "correct" | "incorrect" | "ungraded", or null
// when the response does not fit the step's canonical response shape.
export const evaluateResponse = (step, response) => {
  if (!isObject(step) || !isObject(response)) return null;
  const type = canonicalType(step);
  let outcome = null;
  if (type === "choice") {
    const value = response.value;
    const options = list(step.options || step.choices).map((item) => String(isObject(item) ? item.value ?? item.id ?? item.key : item));
    if (value == null || !options.includes(String(value))) outcome = null;
    else outcome = String(value) === String(choiceExpected(step)) ? "correct" : "incorrect";
  } else if (type === "sequence") {
    const value = response.value;
    const expected = sequenceExpected(step);
    outcome = Array.isArray(value) && Array.isArray(expected)
      ? sameArray(value.map(String), expected.map(String)) ? "correct" : "incorrect"
      : null;
  } else if (type === "matching") {
    outcome = Array.isArray(response.pairs)
      ? sameRelations(step.expected?.pairs, response.pairs, "left", "right") ? "correct" : "incorrect"
      : null;
  } else if (type === "sorting") {
    outcome = Array.isArray(response.assignments)
      ? sameRelations(step.expected?.assignments, response.assignments, "item", "group") ? "correct" : "incorrect"
      : null;
  } else if (type === "counting") {
    const selected = response.selected;
    const itemIds = new Set(list(step.items).map((item) => String(item?.value)));
    const selectedValid = Array.isArray(selected) &&
      new Set(selected.map(String)).size === selected.length &&
      selected.every((value) => itemIds.has(String(value)));
    const estimateValid = step.mode !== "estimate_then_count" || Number.isInteger(response.estimate);
    outcome = Number.isInteger(response.count) && response.count >= 0 && selectedValid && estimateValid
      ? response.count === step.expected?.count ? "correct" : "incorrect"
      : null;
  } else if (type === "drawing") {
    outcome = response.completed === true ? "ungraded" : null;
  } else if (type === "tracing") {
    outcome = response.completed === true && Number.isInteger(response.stroke_count) && response.stroke_count >= 0
      ? "ungraded" : null;
  } else if (type === "oral_response") {
    outcome = response.completed === true && Number.isInteger(response.duration_ms) && response.duration_ms >= 0
      ? "ungraded" : null;
  } else if (type === "numeric_entry") outcome = numericOutcome(step, response);
  else if (type === "expression_entry") outcome = expressionOutcome(step, response);
  else if (type === "coordinate_plane") {
    const expected = step.expected;
    outcome = isObject(expected) && (Array.isArray(response.points) || Array.isArray(response.figures))
      ? geometryCorrect({
          points: list(expected.points),
          figures: list(expected.figures),
          tolerance: expected.tolerance || 0,
        }, response) ? "correct" : "incorrect"
      : null;
  } else if (type === "table_completion") outcome = tableOutcome(step, response);
  else if (type === "text_entry") outcome = textOutcome(step, response);
  else if (type === "pattern") {
    const value = response.value || response.continuation;
    outcome = Array.isArray(value) && Array.isArray(step.expected?.continuation)
      ? sameArray(value, step.expected.continuation) ? "correct" : "incorrect"
      : null;
  } else if (type === "number_line") {
    const field = step.mode === "select" ? "values" : step.mode === "place" ? "positions" : null;
    const expected = field && step.expected?.[field];
    const actual = field && response?.[field];
    const shapeValid = field === "values"
      ? Array.isArray(actual) && actual.every(Number.isFinite)
      : field === "positions"
        ? isObject(actual) && Object.values(actual).every(Number.isFinite)
        : false;
    outcome = shapeValid
      ? (Array.isArray(expected) ? sameArray(actual, expected) : sameValueMap(actual, expected))
        ? "correct" : "incorrect"
      : null;
  } else if (type === "number_sentence") {
    const actual = response?.values;
    const expected = step.expected?.values;
    outcome = isObject(actual) && Object.values(actual).every(Number.isFinite) && isObject(expected)
      ? sameValueMap(actual, expected) ? "correct" : "incorrect"
      : null;
  }
  return outcome ? { type, outcome } : null;
};

// --- Response policy (Learner Work Policy v1 §2–§3) ------------------------

export const COMPLETION_POLICIES = ["attempted", "correct"];
export const RETRY_POLICIES = ["none", "allowed", "until_correct"];
export const FEEDBACK_TIMINGS = [
  "immediate",
  "after_activity",
  "after_submission",
  "after_teacher_review",
];

// Context defaults when a version 1 definition omits the fields. Live
// classroom, homework and practice share one default; assessment hides
// correctness until the assignment is submitted.
const DEFAULT_POLICY = {
  live: { completionPolicy: "attempted", retryPolicy: "allowed", feedbackTiming: "immediate" },
  assessment: { completionPolicy: "attempted", retryPolicy: "none", feedbackTiming: "after_submission" },
};

export const isValidPolicy = (policy) =>
  isObject(policy) &&
  FEEDBACK_TIMINGS.includes(policy.feedbackTiming) &&
  ((policy.completionPolicy === "attempted" && ["none", "allowed"].includes(policy.retryPolicy)) ||
    (policy.completionPolicy === "correct" && policy.retryPolicy === "until_correct"));

// The effective policy, or null when the combination is not valid in v1.
// Never inferred from primitive type, subject, lesson or expected answers.
export const resolvePolicy = (definition, context, override = {}) => {
  const fallback = DEFAULT_POLICY[context] || DEFAULT_POLICY.live;
  const policy = {
    completionPolicy:
      override.completionPolicy ?? definition?.completion_policy ?? fallback.completionPolicy,
    retryPolicy: override.retryPolicy ?? definition?.retry_policy ?? fallback.retryPolicy,
    feedbackTiming:
      override.feedbackTiming ?? definition?.feedback_timing ?? fallback.feedbackTiming,
  };
  return isValidPolicy(policy) ? policy : null;
};

export const stepCompletes = (policy, outcome) =>
  policy.completionPolicy === "attempted" || outcome === "correct";
